import React, { useRef, useCallback, useState } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from './ui/context-menu';
import * as FeatherIcons from 'react-icons/fi';
import {
  FaGlobe, FaFacebook, FaTwitter, FaInstagram, FaGoogle, FaApple,
  FaLinkedin, FaGithub, FaYoutube, FaTiktok
} from 'react-icons/fa';
import { replaceTemplateVariables } from '../utils/dataUtils';

/**
 * Canvas Component - Main editing area
 * Supports drag-and-drop, selection, and visual editing
 */
const Canvas = ({
  screen,
  selectedElement,
  onSelectElement,
  onElementMove,
  onElementResize,
  onDropComponent,
  onAction,
  onDeleteElement,
  onDuplicateElement,
  onRenameElement,
  onUpdateElement,
  isPreview = false,
  isFreePlan = false,
  globalSettings,
  collectedData = {}, // Data collected from inputs across screens
  onDataChange, // Callback when input data changes
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const dragStateRef = useRef({ isDragging: false, elementId: null, startX: 0, startY: 0, elementStartX: 0, elementStartY: 0 });
  const resizeStateRef = useRef({ isResizing: false, handle: null, elementId: null, startX: 0, startY: 0, startWidth: 0, startHeight: 0, startElementX: 0, startElementY: 0 });

  // Inline editing state
  const [editingElementId, setEditingElementId] = useState(null);
  const [editingText, setEditingText] = useState('');

  // Track actual rendered heights for text elements (for selection overlay)
  const [elementHeights, setElementHeights] = useState({});

  const handleDoubleClick = (element, e) => {
    if (isPreview) return;
    e.stopPropagation();
    if (element.type === 'text' || element.type === 'button') {
      setEditingElementId(element.id);
      setEditingText(element.content || '');
    }
  };

  const handleTextSave = () => {
    if (editingElementId) {
      const element = screen.elements.find(el => el.id === editingElementId);
      if (element && onUpdateElement) {
        onUpdateElement({
          ...element,
          content: editingText
        });
      }
      setEditingElementId(null);
    }
  };

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (isPreview) return;

      // Delete element on Delete or Backspace
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElement) {
        // Don't delete if user is typing in an input or contentEditable
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;

        onDeleteElement(selectedElement.id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPreview, selectedElement, onDeleteElement]);

  // Calculate dimensions - responsive to fit container
  // Base dimensions for iPhone-like screen
  // Base dimensions for iPhone-like screen
  const baseWidth = 375;
  const baseHeight = screen?.height || 812; // Default height if missing
  const aspectRatio = baseHeight / baseWidth;

  // Container size state for responsive sizing
  const [containerSize, setContainerSize] = useState({ width: baseWidth, height: baseWidth * aspectRatio });

  // We'll calculate the actual size in the render based on container
  // For now, use base dimensions for scale calculations
  const scaleFactor = (screen && screen.width) ? (containerSize.width / screen.width) : 1;

  const handleElementClick = useCallback((element, e) => {
    e.stopPropagation();
    onSelectElement(element);
  }, [onSelectElement]);


  const handleMouseDown = useCallback((element, e) => {
    if (isPreview || !screen) return;
    e.stopPropagation();
    e.preventDefault();

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    dragStateRef.current = {
      isDragging: true,
      elementId: element.id,
      startX: e.clientX,
      startY: e.clientY,
      elementStartX: element.x,
      elementStartY: element.y,
    };

    onSelectElement(element);
  }, [isPreview, screen, onSelectElement]);

  const handleResizeMouseDown = useCallback((element, handle, e) => {
    if (isPreview || !screen) return;
    e.stopPropagation();
    e.preventDefault();

    resizeStateRef.current = {
      isResizing: true,
      handle,
      elementId: element.id,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: element.width,
      startHeight: element.height,
      startElementX: element.x,
      startElementY: element.y,
      // Store current values for final commit
      currentWidth: element.width,
      currentHeight: element.height,
      currentX: element.x,
      currentY: element.y
    };
  }, [isPreview, screen]);

  const handleMouseMove = useCallback((e) => {
    if (isPreview || !screen) return;

    // Handle Resizing (Direct DOM Manipulation for Performance)
    if (resizeStateRef.current.isResizing) {
      const { handle, elementId, startX, startY, startWidth, startHeight, startElementX, startElementY } = resizeStateRef.current;
      const deltaX = (e.clientX - startX) / scaleFactor;
      const deltaY = (e.clientY - startY) / scaleFactor;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startElementX;
      let newY = startElementY;

      if (handle.includes('e')) newWidth = Math.max(10, startWidth + deltaX);
      if (handle.includes('w')) {
        newWidth = Math.max(10, startWidth - deltaX);
        newX = startElementX + (startWidth - newWidth);
      }
      if (handle.includes('s')) newHeight = Math.max(10, startHeight + deltaY);
      if (handle.includes('n')) {
        newHeight = Math.max(10, startHeight - deltaY);
        newY = startElementY + (startHeight - newHeight);
      }

      // Update refs for final commit
      resizeStateRef.current.currentWidth = newWidth;
      resizeStateRef.current.currentHeight = newHeight;
      resizeStateRef.current.currentX = newX;
      resizeStateRef.current.currentY = newY;

      // Direct DOM update
      const elementDOM = document.getElementById(`element-${elementId}`);
      const overlayDOM = document.getElementById('selection-overlay');

      if (elementDOM) {
        elementDOM.style.width = `${newWidth}px`;
        elementDOM.style.height = `${newHeight}px`;
        elementDOM.style.left = `${newX}px`;
        elementDOM.style.top = `${newY}px`;
      }

      if (overlayDOM) {
        overlayDOM.style.width = `${newWidth * scaleFactor}px`;
        overlayDOM.style.height = `${newHeight * scaleFactor}px`;
        overlayDOM.style.left = `${newX * scaleFactor}px`;
        overlayDOM.style.top = `${newY * scaleFactor}px`;
      }
      return;
    }

    // Handle Dragging
    if (!dragStateRef.current.isDragging) return;

    const deltaX = (e.clientX - dragStateRef.current.startX) / scaleFactor;
    const deltaY = (e.clientY - dragStateRef.current.startY) / scaleFactor;

    const element = screen.elements.find(el => el.id === dragStateRef.current.elementId);
    if (!element) return;

    const newX = Math.max(0, Math.min(screen.width - element.width, dragStateRef.current.elementStartX + deltaX));
    const newY = Math.max(0, Math.min(screen.height - element.height, dragStateRef.current.elementStartY + deltaY));

    onElementMove(dragStateRef.current.elementId, { x: newX, y: newY });
  }, [isPreview, scaleFactor, screen, onElementMove]);

  const handleMouseUp = useCallback(() => {
    if (resizeStateRef.current.isResizing) {
      const { elementId, currentWidth, currentHeight, currentX, currentY } = resizeStateRef.current;
      onElementResize(elementId, { width: currentWidth, height: currentHeight }, { x: currentX, y: currentY });
    }

    dragStateRef.current.isDragging = false;
    resizeStateRef.current.isResizing = false;
  }, [onElementResize]);

  // Drag and drop handlers for adding components from sidebar
  const handleDragOver = useCallback((e) => {
    if (isPreview || !screen || !onDropComponent) return;
    e.preventDefault();
    e.stopPropagation();
  }, [isPreview, screen, onDropComponent]);

  const handleDrop = useCallback((e) => {
    if (isPreview || !screen || !onDropComponent) return;
    e.preventDefault();
    e.stopPropagation();

    try {
      const componentData = JSON.parse(e.dataTransfer.getData('application/json'));

      // Get the canvas element's bounding rect
      const canvasElement = canvasRef.current;
      if (!canvasElement) {
        console.warn('Canvas ref not available');
        return;
      }

      const canvasRect = canvasElement.getBoundingClientRect();

      // Get the actual rendered canvas dimensions
      const renderedWidth = canvasRect.width;
      const renderedHeight = canvasRect.height;

      // Calculate the scale factor based on actual rendered size
      const actualScale = screen ? (renderedWidth / screen.width) : 1;

      // Calculate drop position relative to canvas
      // e.clientX/Y are viewport coordinates, subtract canvas position to get canvas-relative coordinates
      // Account for border (2px) if not preview
      const borderOffset = isPreview ? 0 : 2;
      const dropX = e.clientX - canvasRect.left - borderOffset;
      const dropY = e.clientY - canvasRect.top - borderOffset;

      // Convert canvas-relative coordinates to screen coordinates (divide by scale)
      const screenX = dropX / actualScale;
      const screenY = dropY / actualScale;

      // Get component dimensions
      const componentWidth = componentData.defaultProps.width;
      const componentHeight = componentData.defaultProps.height;

      // Position component centered at drop point (center at drop position, clamped to screen bounds)
      const finalX = Math.max(0, Math.min(screen.width - componentWidth, screenX - (componentWidth / 2)));
      const finalY = Math.max(0, Math.min(screen.height - componentHeight, screenY - (componentHeight / 2)));

      // Create new element at drop position
      const newElement = {
        id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: componentData.type,
        name: componentData.name,
        x: finalX,
        y: finalY,
        width: componentWidth,
        height: componentHeight,
        content: componentData.defaultProps.content || '',
        styles: { ...componentData.defaultProps.styles },
        action: componentData.defaultProps.action || 'none',
        imageUrl: componentData.defaultProps.imageUrl || null,
        options: componentData.defaultProps.options || [],
        selectedOption: componentData.defaultProps.selectedOption || null,
        selectedIndices: componentData.defaultProps.selectedIndices || [],
        selectionType: componentData.defaultProps.selectionType || 'single', // 'single' or 'multiple'
        iconName: componentData.defaultProps.iconName || 'star',
        iconSize: componentData.defaultProps.iconSize || 24,
        totalScreens: componentData.defaultProps.totalScreens || 3,
        currentScreen: componentData.defaultProps.currentScreen || 0,
        progress: componentData.defaultProps.progress || 0,
        rotation: 0,
      };

      onDropComponent(newElement);
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  }, [isPreview, screen, onDropComponent]);

  // Add global mouse event listeners
  React.useEffect(() => {
    if (isPreview || !screen) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPreview, screen, handleMouseMove, handleMouseUp]);

  // Calculate responsive size to fit container
  React.useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const maxWidth = containerRect.width - 40; // 20px padding on each side
      const maxHeight = containerRect.height - 40; // 20px padding on each side

      // Calculate size maintaining aspect ratio
      let width = baseWidth;
      let height = baseWidth * aspectRatio;

      // Scale down if needed to fit
      if (width > maxWidth) {
        width = maxWidth;
        height = width * aspectRatio;
      }
      if (height > maxHeight) {
        height = maxHeight;
        width = height / aspectRatio;
      }

      setContainerSize({ width, height });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [aspectRatio, baseWidth]);

  // Early return AFTER all hooks
  if (!screen) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb',
        color: '#9ca3af',
        fontSize: '14px'
      }}>
        Create a new screen to get started
      </div>
    );
  }

  const renderCanvas = (containerWidth = baseWidth, containerHeight = baseWidth * aspectRatio) => {
    const actualScaleFactor = screen ? (containerWidth / screen.width) : 1;

    const canvasStyle = {
      width: `${containerWidth}px`,
      height: `${containerHeight}px`,
      position: 'relative',
      backgroundColor: screen.backgroundColor || '#ffffff',
      borderRadius: isPreview ? '0' : '12px',
      overflow: 'hidden',
      boxShadow: isPreview ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      border: isPreview ? 'none' : '2px solid #e5e7eb',
      fontFamily: globalSettings?.fontFamily || 'Inter, sans-serif',
    };

    return (
      <div
        ref={canvasRef}
        style={canvasStyle}
        onClick={(e) => {
          onSelectElement(null);
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Background layer */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${screen.width}px`,
            height: `${screen.height}px`,
            transform: `scale(${actualScaleFactor})`,
            transformOrigin: 'top left',
            backgroundColor: screen.backgroundColor || '#ffffff',
          }}
        >
          {/* Hybrid mode: Render Figma image as background */}
          {screen.type === 'hybrid' && screen.backgroundImage && (
            <img
              src={screen.backgroundImage}
              alt="Figma design"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>


        {/* Elements layer */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${screen.width}px`,
            height: `${screen.height}px`,
            transform: `scale(${actualScaleFactor})`,
            transformOrigin: 'top left',
          }}
        >
          {(screen.elements || []).map((element) => {
            const isSelected = selectedElement?.id === element.id;

            // Render element based on type
            let elementContent = null;

            if (element.type === 'text') {
              const isEditing = editingElementId === element.id;

              const textElement = isEditing ? (
                <textarea
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onBlur={handleTextSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleTextSave();
                    }
                  }}
                  autoFocus
                  style={{
                    ...element.styles,
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    resize: 'none',
                    border: '2px solid #3b82f6',
                    outline: 'none',
                    background: 'transparent',
                    overflow: 'hidden',
                    padding: 0,
                    margin: 0,
                    fontFamily: element.styles?.fontFamily || 'inherit',
                  }}
                />
              ) : (
                <div
                  // ref removed to prevent infinite loop

                  style={{
                    ...element.styles,
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    minHeight: `${element.height}px`,
                    height: 'auto',
                    margin: 0,
                    padding: element.styles?.padding || 0,
                    boxSizing: 'border-box',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflow: 'visible',
                    cursor: isPreview ? 'default' : 'move',
                    userSelect: 'none',
                    border: isSelected && !isPreview ? '2px solid #3b82f6' : 'none',
                    outline: isSelected && !isPreview ? '2px dashed rgba(59, 130, 246, 0.5)' : 'none',
                    outlineOffset: '2px',
                  }}
                  onClick={(e) => handleElementClick(element, e)}
                  onMouseDown={(e) => handleMouseDown(element, e)}
                  onDoubleClick={(e) => handleDoubleClick(element, e)}
                  dangerouslySetInnerHTML={{
                    __html: isPreview
                      ? replaceTemplateVariables(element.content || 'Text', collectedData)
                      : (element.content || 'Text')
                  }}
                />
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {textElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : textElement;
            } else if (element.type === 'button') {
              const buttonElement = (
                <button
                  style={{
                    ...element.styles,
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    cursor: isPreview ? 'pointer' : 'move',
                    border: isSelected && !isPreview
                      ? '2px solid #3b82f6'
                      : element.isOverlay && !isPreview
                        ? '2px dotted #ef4444' // Red dotted border for overlays in editor
                        : element.styles.border || 'none',
                    backgroundColor: element.isOverlay
                      ? (isPreview ? 'transparent' : 'rgba(239, 68, 68, 0.1)') // Transparent in preview, red tint in editor
                      : element.styles.backgroundColor,
                    outline: isSelected && !isPreview ? '2px dashed rgba(59, 130, 246, 0.5)' : 'none',
                    outlineOffset: '2px',
                  }}
                  onClick={(e) => {
                    if (!isPreview) {
                      handleElementClick(element, e);
                    } else {
                      // Handle button action in preview
                      e.stopPropagation();
                      if (element.action && element.action !== 'none' && onAction) {
                        onAction(element.action);
                      }
                    }
                  }}
                  onMouseDown={(e) => handleMouseDown(element, e)}
                  dangerouslySetInnerHTML={{
                    __html: isPreview
                      ? replaceTemplateVariables(element.content || 'Button', collectedData)
                      : (element.content || 'Button')
                  }}
                />
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {buttonElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : buttonElement;
            } else if (element.type === 'image') {
              const imageElement = (
                <div
                  style={{
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    border: isSelected && !isPreview ? '2px solid #3b82f6' : '1px dashed #d1d5db',
                    outline: isSelected && !isPreview ? '2px dashed rgba(59, 130, 246, 0.5)' : 'none',
                    outlineOffset: '2px',
                    cursor: isPreview ? 'default' : 'move',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f3f4f6',
                    overflow: 'hidden',
                  }}
                  onClick={(e) => handleElementClick(element, e)}
                  onMouseDown={(e) => handleMouseDown(element, e)}
                >
                  {element.imageUrl ? (
                    <img
                      src={element.imageUrl}
                      alt={element.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        ...element.styles,
                      }}
                    />
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>Image</span>
                  )}
                </div>
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {imageElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : imageElement;
            } else if (element.type === 'multiple-choice') {
              const isMultiple = element.selectionType === 'multiple';

              const choiceElement = (
                <div
                  style={{
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    border: isSelected && !isPreview ? '2px solid #3b82f6' : 'none',
                    outline: isSelected && !isPreview ? '2px dashed rgba(59, 130, 246, 0.5)' : 'none',
                    outlineOffset: '2px',
                    cursor: isPreview ? 'default' : 'move',
                    padding: '8px',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    ...element.styles,
                  }}
                  onClick={(e) => handleElementClick(element, e)}
                  onMouseDown={(e) => handleMouseDown(element, e)}
                >
                  {(element.options || []).map((option, idx) => {
                    const isSelectedOption = isMultiple
                      ? (element.selectedIndices || []).includes(idx)
                      : element.selectedOption === idx;

                    return (
                      <div
                        key={idx}
                        onClick={(e) => {
                          if (!isPreview) return;
                          e.stopPropagation();

                          if (isMultiple) {
                            const currentIndices = element.selectedIndices || [];
                            const newIndices = currentIndices.includes(idx)
                              ? currentIndices.filter(i => i !== idx)
                              : [...currentIndices, idx];
                            onUpdateElement?.({ ...element, selectedIndices: newIndices });
                          } else {
                            onUpdateElement?.({ ...element, selectedOption: idx });
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          backgroundColor: isSelectedOption ? (element.styles?.activeBackgroundColor || '#eff6ff') : (element.styles?.optionBackgroundColor || '#ffffff'),
                          border: `1px solid ${isSelectedOption ? (element.styles?.activeBorderColor || '#3b82f6') : (element.styles?.borderColor || '#e5e7eb')}`,
                          borderRadius: element.styles?.optionBorderRadius || '8px',
                          cursor: isPreview ? 'pointer' : 'default',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        }}
                      >
                        <span style={{
                          fontWeight: isSelectedOption ? '600' : '400',
                          color: isSelectedOption ? (element.styles?.activeTextColor || '#1f2937') : (element.styles?.textColor || '#4b5563'),
                          fontSize: element.styles?.fontSize || '14px',
                        }}>
                          {option}
                        </span>

                        {/* Selection Indicator */}
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: isMultiple ? '4px' : '50%',
                          border: `2px solid ${isSelectedOption ? (element.styles?.activeColor || '#3b82f6') : '#d1d5db'}`,
                          backgroundColor: isSelectedOption ? (element.styles?.activeColor || '#3b82f6') : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          {isSelectedOption && (
                            <FeatherIcons.FiCheck size={12} color="white" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {choiceElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : choiceElement;
            } else if (element.type === 'icon') {
              // Brand icons map (non-Feather)
              const brandIconMap = {
                globe: FaGlobe,
                facebook: FaFacebook,
                twitter: FaTwitter,
                instagram: FaInstagram,
                google: FaGoogle,
                apple: FaApple,
                linkedin: FaLinkedin,
                github: FaGithub,
                youtube: FaYoutube,
                tiktok: FaTiktok,
              };

              // Try to get Feather icon dynamically
              // Convert iconName to PascalCase with Fi prefix (e.g., 'heart' -> 'FiHeart')
              const iconKey = element.iconName
                ? 'Fi' + element.iconName.charAt(0).toUpperCase() + element.iconName.slice(1).replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                : 'FiStar';

              const IconComponent = FeatherIcons[iconKey] || brandIconMap[element.iconName] || FeatherIcons.FiStar;
              const iconElement = (
                <div
                  style={{
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    cursor: isPreview && element.action ? 'pointer' : isPreview ? 'default' : 'move',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: element.styles?.color || '#3b82f6',
                    ...element.styles,
                  }}
                  onClick={(e) => {
                    if (isPreview && element.action && onAction) {
                      e.stopPropagation();
                      onAction(element.action);
                    } else {
                      handleElementClick(element, e);
                    }
                  }}
                  onMouseDown={(e) => handleMouseDown(element, e)}
                >
                  <IconComponent size={element.iconSize || 24} />
                </div>
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {iconElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : iconElement;
            } else if (element.type === 'dots-indicator') {
              // Dots indicator for showing screen progress
              const totalScreens = element.totalScreens || 3;
              const currentScreen = element.currentScreen || 0;
              const dotsElement = (
                <div
                  style={{
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    border: isSelected && !isPreview ? '2px solid #3b82f6' : '1px dashed #d1d5db',
                    outline: isSelected && !isPreview ? '2px dashed rgba(59, 130, 246, 0.5)' : 'none',
                    outlineOffset: '2px',
                    cursor: isPreview ? 'default' : 'move',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: element.styles?.gap || '8px',
                    ...element.styles,
                  }}
                  onClick={(e) => handleElementClick(element, e)}
                  onMouseDown={(e) => handleMouseDown(element, e)}
                >
                  {Array.from({ length: totalScreens }).map((_, idx) => (
                    <div
                      key={idx}
                      style={{
                        width: element.styles?.dotSize || '8px',
                        height: element.styles?.dotSize || '8px',
                        borderRadius: '50%',
                        backgroundColor: idx === currentScreen
                          ? (element.styles?.activeColor || '#3b82f6')
                          : (element.styles?.inactiveColor || '#d1d5db'),
                        transition: 'all 0.2s',
                      }}
                    />
                  ))}
                </div>
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {dotsElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : dotsElement;
            } else if (element.type === 'progress-bar') {
              // Progress bar component
              const progress = element.progress || 0; // 0-100
              const progressElement = (
                <div
                  style={{
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    border: isSelected && !isPreview ? '2px solid #3b82f6' : '1px dashed #d1d5db',
                    outline: isSelected && !isPreview ? '2px dashed rgba(59, 130, 246, 0.5)' : 'none',
                    outlineOffset: '2px',
                    cursor: isPreview ? 'default' : 'move',
                    backgroundColor: element.styles?.backgroundColor || '#f3f4f6',
                    borderRadius: element.styles?.borderRadius || '4px',
                    overflow: 'hidden',
                    ...element.styles,
                  }}
                  onClick={(e) => handleElementClick(element, e)}
                  onMouseDown={(e) => handleMouseDown(element, e)}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      height: '100%',
                      backgroundColor: element.styles?.fillColor || '#3b82f6',
                      backgroundImage: element.styles?.striped
                        ? 'linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)'
                        : 'none',
                      backgroundSize: '1rem 1rem',
                      animation: element.styles?.animated ? 'progress-bar-stripes 1s linear infinite' : 'none',
                      transition: 'width 0.3s ease',
                      borderRadius: element.styles?.borderRadius || '4px',
                    }}
                  />
                  <style>{`
                    @keyframes progress-bar-stripes {
                      0% { background-position: 1rem 0; }
                      100% { background-position: 0 0; }
                    }
                  `}</style>
                </div>
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {progressElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : progressElement;
            } else if (element.type === 'input') {
              const inputElement = (
                <div
                  style={{
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    cursor: isPreview ? 'default' : 'move',
                  }}
                  onClick={(e) => handleElementClick(element, e)}
                  onMouseDown={(e) => handleMouseDown(element, e)}
                >
                  {element.label && (
                    <label style={{
                      fontSize: '12px',
                      color: '#374151',
                      fontWeight: '500',
                      pointerEvents: 'none',
                    }}>
                      {element.label}
                    </label>
                  )}
                  <input
                    type="text"
                    placeholder={element.placeholder || ''}
                    disabled={!isPreview}
                    value={isPreview && element.variableName ? (collectedData[element.variableName] || '') : undefined}
                    onChange={(e) => {
                      if (isPreview && element.variableName && onDataChange) {
                        onDataChange({
                          ...collectedData,
                          [element.variableName]: e.target.value
                        });
                      }
                    }}
                    style={{
                      flex: 1,
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      borderWidth: element.styles?.borderWidth || '1px',
                      borderStyle: 'solid',
                      borderColor: element.styles?.borderColor || '#e5e7eb',
                      borderRadius: element.styles?.borderRadius || '6px',
                      backgroundColor: element.isOverlay
                        ? (isPreview ? 'transparent' : 'rgba(239, 68, 68, 0.1)')
                        : element.styles?.backgroundColor || '#ffffff',
                      color: element.styles?.color || '#1f2937',
                      outline: 'none',
                      pointerEvents: isPreview ? 'auto' : 'none',
                      ...element.styles,
                    }}
                  />
                  {/* Selection border for editor */}
                  {(isSelected || (element.isOverlay && !isPreview)) && (
                    <div style={{
                      position: 'absolute',
                      inset: -2,
                      border: isSelected
                        ? '2px solid #3b82f6'
                        : '2px dotted #ef4444',
                      borderRadius: '4px',
                      pointerEvents: 'none',
                    }} />
                  )}
                </div>
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {inputElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : inputElement;
            } else if (element.type === 'shape') {
              // Shape rendering (rectangle, circle, etc.)
              const shapeElement = (
                <div
                  style={{
                    position: 'absolute',
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
                    transformOrigin: 'center',
                    cursor: isPreview ? 'default' : 'move',
                    pointerEvents: isPreview ? 'none' : 'auto',
                  }}
                  onClick={(e) => {
                    if (!isPreview) {
                      handleElementClick(element, e);
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!isPreview) {
                      handleMouseDown(element, e);
                    }
                  }}
                  onDoubleClick={(e) => {
                    if (e && e.stopPropagation) e.stopPropagation();
                    if (handleDoubleClick) handleDoubleClick(element, e);
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: element.styles?.backgroundColor || '#3b82f6',
                      borderRadius: element.shapeType === 'ellipse' ? '50%' : (element.styles?.borderRadius || '8px'),
                      border: element.styles?.border || 'none',
                      borderWidth: element.styles?.borderWidth || '0',
                      borderStyle: element.styles?.borderWidth && parseInt(element.styles.borderWidth) > 0 ? 'solid' : 'none',
                      borderColor: element.styles?.borderColor || '#d1d5db',
                      opacity: element.styles?.opacity !== undefined ? element.styles.opacity : 1,
                      boxShadow: element.styles?.boxShadow || 'none',
                      ...element.styles,
                    }}
                  />
                  {/* Selection border */}
                  {isSelected && !isPreview && (
                    <div style={{
                      position: 'absolute',
                      inset: -2,
                      border: '2px solid #3b82f6',
                      borderRadius: element.shapeType === 'ellipse' ? '50%' : (element.styles?.borderRadius || '8px'),
                      pointerEvents: 'none',
                    }} />
                  )}
                </div>
              );

              elementContent = !isPreview ? (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    {shapeElement}
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => onRenameElement?.(element)}>
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => onDuplicateElement?.(element)}>
                      Duplicate
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      className="text-destructive"
                      onClick={() => onDeleteElement?.(element.id)}
                    >
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ) : shapeElement;
            }

            // Fallback for unknown types or if elementContent is null
            if (!elementContent) return null;

            if (!React.isValidElement(elementContent)) {
              return null;
            }

            return (
              <React.Fragment key={element.id}>
                {React.cloneElement(elementContent, { id: `element-${element.id}` })}
              </React.Fragment>
            );
          })}
        </div>

        {/* Safe Area Indicators - Rendered AFTER elements to appear on top */}
        {!isPreview && (screen.styles?.paddingTop || screen.styles?.paddingBottom) && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${screen.width}px`,
              height: `${screen.height}px`,
              transform: `scale(${actualScaleFactor})`,
              transformOrigin: 'top left',
              pointerEvents: 'none',
              zIndex: 1000, // Very high z-index to ensure it's always on top
            }}
          >
            {(() => {
              // Calculate overlap dynamically during render
              const safeTop = parseInt(screen.styles.paddingTop || '0');
              const safeBottom = parseInt(screen.styles.paddingBottom || '0');

              // Find the element being moved/resized
              const activeElement = selectedElement
                ? screen.elements.find(el => el.id === selectedElement.id)
                : null;

              // Check if we are actively dragging or resizing
              const isInteracting = dragStateRef.current.isDragging || resizeStateRef.current.isResizing;

              // If interacting, check for overlap
              if (activeElement && isInteracting) {
                const elTop = activeElement.y;
                const elBottom = activeElement.y + activeElement.height;
                const screenHeight = screen.height;

                const showTop = safeTop > 0 && elTop < safeTop;
                const showBottom = safeBottom > 0 && elBottom > (screenHeight - safeBottom);

                return (
                  <>
                    {showTop && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: screen.styles.paddingTop,
                          backgroundColor: 'rgba(255, 59, 48, 0.15)',
                          borderBottom: '2px dashed rgba(255, 59, 48, 0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(255, 59, 48, 0.9)',
                          fontSize: '11px',
                          fontWeight: '600',
                          animation: 'fadeIn 0.2s ease-in-out',
                          textShadow: '0 0 4px rgba(255, 255, 255, 0.8)',
                        }}
                      >
                        ⚠️ Safe Area (Top)
                      </div>
                    )}
                    {showBottom && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          height: screen.styles.paddingBottom,
                          backgroundColor: 'rgba(255, 59, 48, 0.15)',
                          borderTop: '2px dashed rgba(255, 59, 48, 0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(255, 59, 48, 0.9)',
                          fontSize: '11px',
                          fontWeight: '600',
                          animation: 'fadeIn 0.2s ease-in-out',
                          textShadow: '0 0 4px rgba(255, 255, 255, 0.8)',
                        }}
                      >
                        ⚠️ Safe Area (Bottom)
                      </div>
                    )}
                  </>
                );
              }

              // If NOT interacting, show subtle border indicators
              return (
                <>
                  {safeTop > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: screen.styles.paddingTop,
                        borderBottom: '1px dashed rgba(59, 130, 246, 0.3)',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                  {safeBottom > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: screen.styles.paddingBottom,
                        borderTop: '1px dashed rgba(59, 130, 246, 0.3)',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </>
              );
            })()}
          </div>
        )}


        {/* Selection indicator and Resize Handles */}
        {selectedElement && !isPreview && (
          <>
            {/* Selection Overlay & Resize Handles */}
            <div
              id="selection-overlay"
              style={{
                position: 'absolute',
                left: `${selectedElement.x * scaleFactor}px`,
                top: `${selectedElement.y * scaleFactor}px`,
                width: `${selectedElement.width * scaleFactor}px`,
                height: `${(selectedElement.type === 'text' && elementHeights[selectedElement.id]
                  ? elementHeights[selectedElement.id]
                  : selectedElement.height) * scaleFactor}px`,
                border: '1px solid #3b82f6',
                pointerEvents: 'none', // Let clicks pass through to element
                zIndex: 1000,
              }}
            >
              {/* Label */}
              <div
                style={{
                  position: 'absolute',
                  top: '-24px',
                  left: '0',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedElement.name || selectedElement.type}
              </div>

              {/* Resize Handles */}
              {(() => {
                const handleSize = 8;
                const handles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

                return handles.map(handle => {
                  let style = {
                    position: 'absolute',
                    width: `${handleSize}px`,
                    height: `${handleSize}px`,
                    backgroundColor: '#ffffff',
                    border: '1px solid #3b82f6',
                    borderRadius: '50%',
                    pointerEvents: 'auto', // Enable clicking on handles
                    zIndex: 1001,
                  };

                  if (handle === 'nw') { style.top = -4; style.left = -4; style.cursor = 'nwse-resize'; }
                  if (handle === 'n') { style.top = -4; style.left = '50%'; style.transform = 'translateX(-50%)'; style.cursor = 'ns-resize'; }
                  if (handle === 'ne') { style.top = -4; style.right = -4; style.cursor = 'nesw-resize'; }
                  if (handle === 'e') { style.top = '50%'; style.right = -4; style.transform = 'translateY(-50%)'; style.cursor = 'ew-resize'; }
                  if (handle === 'se') { style.bottom = -4; style.right = -4; style.cursor = 'nwse-resize'; }
                  if (handle === 's') { style.bottom = -4; style.left = '50%'; style.transform = 'translateX(-50%)'; style.cursor = 'ns-resize'; }
                  if (handle === 'sw') { style.bottom = -4; style.left = -4; style.cursor = 'nesw-resize'; }
                  if (handle === 'w') { style.top = '50%'; style.left = -4; style.transform = 'translateY(-50%)'; style.cursor = 'ew-resize'; }

                  return (
                    <div
                      key={handle}
                      onMouseDown={(e) => handleResizeMouseDown(selectedElement, handle, e)}
                      style={style}
                    />
                  );
                });
              })()}
            </div>
          </>
        )}

        {/* Test Mode Badge */}
        {isFreePlan && (
          <div style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            pointerEvents: 'none',
            zIndex: 9999,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            backdropFilter: 'blur(2px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            Test Mode
          </div>
        )}
      </div>
    );
  };

  // If preview mode, wrap in phone mockup
  if (isPreview) {
    return (
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          padding: '20px',
          backgroundColor: '#f3f4f6', // Subtle background to make phone stand out
        }}
      >
        <div style={{
          position: 'relative',
          width: `${containerSize.width}px`,
          height: `${containerSize.height}px`,
          maxWidth: '100%',
          maxHeight: '100%',
          filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.15)) drop-shadow(0 8px 16px rgba(0, 0, 0, 0.1))',
        }}>
          {/* Phone mockup image */}
          <img
            src="/phonemockup.png"
            alt="Phone mockup"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              zIndex: 1,
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))',
            }}
            onError={(e) => {
              // Fallback if image doesn't load - show a phone frame
              e.target.style.display = 'none';
            }}
          />
          {/* Screen area - positioned over the phone mockup */}
          <div style={{
            position: 'absolute',
            width: `${containerSize.width * 0.9}px`, // Adjust based on your mockup's screen area
            height: `${containerSize.height * 0.9}px`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            overflow: 'hidden',
            zIndex: 2,
            borderRadius: '20px', // Adjust based on your phone's screen radius
          }}>
            {renderCanvas(containerSize.width * 0.9, containerSize.height * 0.9)}
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - responsive canvas
  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: '#f3f4f6', // Add background color to match preview mode or similar
      }}
    >
      {renderCanvas(containerSize.width, containerSize.height)}

      {/* Dynamic Font Import */}
      {globalSettings?.fontFamily && (
        <style>
          {`@import url('https://fonts.googleapis.com/css2?family=${globalSettings.fontFamily.replace(/ /g, '+')}:wght@400;500;600;700&display=swap');`}
        </style>
      )}
    </div>
  );
};

export default Canvas;
